import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core'
import React, { useCallback, useMemo } from 'react'
import { Organization } from '../../../shared/src/data/roleHierarchy/organization'
import { RoleHierarchy } from '../../../shared/src/data/roleHierarchy/roleHierarchy'
import { Branch } from '../../../shared/src/data/roleHierarchy/branch'

type RoleHierarchySelectorProps = {
  roleHierarchies: RoleHierarchy[]
  selectedRoleHierarchy: RoleHierarchy | undefined
  setSelectedRoleHierarchy: (roleHierarchy: RoleHierarchy) => void
}

export function RoleHierarchySelector({
  roleHierarchies,
  selectedRoleHierarchy,
  setSelectedRoleHierarchy,
}: RoleHierarchySelectorProps): JSX.Element {
  const organizations = useMemo<Organization[]>(() => {
    const nonUniqueOrganizations =
      roleHierarchies?.map((a) => a.organization) || []
    return nonUniqueOrganizations.filter(
      (a1, i) =>
        nonUniqueOrganizations.findIndex((a2) => a1.id === a2.id) === i,
    )
  }, [roleHierarchies])

  const branchesInSelectedOrganization = useMemo<Branch[]>(
    () =>
      roleHierarchies
        ?.filter(
          (a) => a.organization?.id === selectedRoleHierarchy?.organization.id,
        )
        .map((a) => a.branch)
        .sort((b, c) => (b?.name > c?.name ? 1 : -1)) || [],
    [roleHierarchies, selectedRoleHierarchy],
  )

  const setSelectedOrganizationByOrganizationId = useCallback(
    (id: string) => {
      const roleHierarchiesInOrganization = roleHierarchies.find(
        (a) => a.organization.id === id,
      )!
      setSelectedRoleHierarchy(roleHierarchiesInOrganization)
    },
    [roleHierarchies, setSelectedRoleHierarchy],
  )

  const setSelectedRoleHierarchyByBranchById = useCallback(
    (id: string) => {
      const roleHierarchy = roleHierarchies.find(
        (a) =>
          a.branch.id === id &&
          a.organization?.id === selectedRoleHierarchy?.organization.id,
      )!
      setSelectedRoleHierarchy(roleHierarchy)
    },
    [roleHierarchies, setSelectedRoleHierarchy, selectedRoleHierarchy],
  )

  return (
    <>
      <FormControl fullWidth color='secondary'>
        <InputLabel>Organization</InputLabel>
        <Select
          value={selectedRoleHierarchy?.organization.id || ''}
          fullWidth
          onChange={(e) =>
            setSelectedOrganizationByOrganizationId(e.target.value as string)
          }
        >
          {organizations.map((org) => (
            <MenuItem key={org.id} value={org.id}>
              {org.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth color='secondary'>
        <InputLabel>Branch</InputLabel>
        <Select
          value={selectedRoleHierarchy?.branch?.id || ''}
          disabled={!selectedRoleHierarchy}
          fullWidth
          onChange={(e) =>
            setSelectedRoleHierarchyByBranchById(e.target.value as string)
          }
        >
          {branchesInSelectedOrganization.map((branch) => (
            <MenuItem key={branch?.id} value={branch.id}>
              {branch?.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  )
}
